// The admin's app shell — what makes the installed admin feel like a phone app
// (owner spec 2026-10-01, docs/superpowers/plans/2026-10-01-app-feel.md › A).
//
// Mounted ONCE in App.tsx, beside the routes, because every admin page renders
// its own AdminLayout (so AdminLayout remounts on each navigation and cannot
// hold a gesture or remember a scroll). Everything here switches itself off
// outside /admin — customer pages are untouched:
//   • viewport-fit=cover + html.adm-app (16px fields) on admin pages only;
//   • keep where I was: last admin page (installed app, < 12 h) and per-page
//     scroll on Back;
//   • 200 ms route entrance (View Transition taps are handled in ShellLink);
//   • left-edge swipe-back, the page following the finger;
//   • pull-to-refresh → every active React Query query refetches;
//   • html[data-adm-typing] while a text field has the keyboard;
//   • night mode: html.adm-dark + the status-bar colour (lib/theme/adminTheme.ts)
//     — on the admin, and for a STAFF member also in the editor and the 3D
//     preview; the choice is the signed-in person's, saved on the server.
// The decisions are pure and unit-tested in lib/appShell/*.test.ts.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { isAdminPath, isIosDevice, isStandalone } from '@/lib/pwa/detect';
import { syncAdminViewport } from '@/lib/pwa/adminHead';
import { adminTheme, applyTheme, inDarkScope, useAdminTheme } from '@/lib/theme/adminTheme';
import { saveThemePref } from '@/lib/theme/themePrefApi';
import { canEnter, guardForPath } from '@/lib/appShell/navModel';
import { pathToRestore, recordLastPath, readScroll, saveScroll } from '@/lib/appShell/lastPath';
import { entranceKeyframes, prefersReducedMotion, routeCommitted, ROUTE_MS, takeViewTransitionFlag } from '@/lib/appShell/routeTransition';
import {
  axisLock, canGoBackInApp, ptrBlocked, ptrPull, PTR_THRESHOLD_PX, shouldCommitBack,
  startsSwipeBack, swipeBackEnabled, swipeBackOffset, type Axis,
} from '@/lib/appShell/gestures';
import { useAuthStore } from '@/store/useAuthStore';
import { usePermissions } from '@/lib/permissionsStore';

const PHONE_QUERY = '(max-width: 767px)';
const isPhone = () => { try { return !!window.matchMedia?.(PHONE_QUERY).matches; } catch { return false; } };
const isTouch = () => (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
const sheetOpen = () => document.body.style.overflow === 'hidden';
const local = (): Storage | null => { try { return window.localStorage; } catch { return null; } };
const session = (): Storage | null => { try { return window.sessionStorage; } catch { return null; } };

type TouchLike = Event & { touches: ArrayLike<{ clientX: number; clientY: number }>; changedTouches?: ArrayLike<{ clientX: number; clientY: number }> };
const pt = (e: Event) => (e as TouchLike).touches?.[0] ?? (e as TouchLike).changedTouches?.[0];

export function AdminAppShell() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const admin = isAdminPath(location.pathname);
  const key = location.pathname + location.search;

  // ── Admin-only document settings ─────────────────────────────────────────
  useLayoutEffect(() => {
    syncAdminViewport(document, location.pathname);
    document.documentElement.classList.toggle('adm-app', admin);
    try { window.history.scrollRestoration = admin ? 'manual' : 'auto'; } catch { /* old browser */ }
  }, [admin, location.pathname]);

  // ── Night mode: whose, and where (2026-10-03) ────────────────────────────
  // The choice belongs to the signed-in PERSON (profiles.theme_pref, sent with
  // the profile; a change is saved back). Until the profile is known the boot
  // cache stands in; a slow or unreachable profile keeps it — never a guess.
  const authLoading = useAuthStore((s) => s.loading);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const profile = useAuthStore((s) => s.profile);
  useEffect(() => {
    adminTheme.setSaver(saveThemePref);
    return () => adminTheme.setSaver(null);
  }, []);
  useEffect(() => {
    if (authLoading) return;
    if (!userId) { adminTheme.identify(null); return; }
    if (!profile || profile.id !== userId) return;
    adminTheme.identify({
      uid: userId,
      staff: profile.role === 'admin' || profile.role === 'staff',
      serverPref: profile.theme_pref,
    });
  }, [authLoading, userId, profile]);

  // On the admin; for a staff member also the editor and the 3D preview
  // (inDarkScope). Repainted live: preference, phone, person, page.
  const { mode, staff } = useAdminTheme();
  const darkScope = inDarkScope(location.pathname, staff);
  useLayoutEffect(() => { applyTheme(document, darkScope, mode); }, [darkScope, mode]);

  // ── Keep where I was ─────────────────────────────────────────────────────
  // Read the remembered page BEFORE this launch records /admin over it.
  const [candidate] = useState(() => {
    const s = local();
    if (!s) return null;
    return pathToRestore(s, Date.now(), {
      pathname: location.pathname, search: location.search, hash: location.hash,
      isInitial: location.key === 'default', standalone: isStandalone(window),
    });
  });
  const restored = useRef(false);
  const role = useAuthStore((s) => s.profile?.role ?? null);
  const { perms, can } = usePermissions();
  useEffect(() => {
    if (!candidate || restored.current) return;
    if (location.key !== 'default') { restored.current = true; return; } // the person already moved
    if (!role || (role === 'staff' && !perms)) return;                     // who is this? wait
    restored.current = true;
    const guard = guardForPath(candidate.split(/[?#]/)[0]);
    if (guard && canEnter(guard, { role, can })) navigate(candidate, { replace: true });
  }, [candidate, location.key, role, perms, can, navigate]);

  useEffect(() => {
    const s = local();
    if (admin && s) recordLastPath(s, key, Date.now());
  }, [admin, key]);

  // Per-page scroll, this session: saved while scrolling, put back on Back.
  // ⚠️ The key is a REF set by the route layout effect below, read when the
  // scroll happens. A per-key listener was still attached when that layout
  // effect scrolled the NEW page to its top — the old page's position was
  // overwritten with 0 before Back could use it (seen in the 390 px check).
  const scrollKey = useRef(key);
  useEffect(() => {
    if (!admin) return;
    let raf = 0;
    const onScroll = () => {
      const k = scrollKey.current;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const s = session();
        if (s && scrollKey.current === k) saveScroll(s, k, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { cancelAnimationFrame(raf); window.removeEventListener('scroll', onScroll); };
  }, [admin]);

  const prevPath = useRef(location.pathname);
  const firstCommit = useRef(true);
  useLayoutEffect(() => {
    routeCommitted();
    scrollKey.current = key;
    const pathChanged = prevPath.current !== location.pathname;
    prevPath.current = location.pathname;
    if (firstCommit.current) { firstCommit.current = false; return; }
    if (!admin) return;

    // Scroll: Back/Forward → where it was; a new page → its top.
    if (navType === 'POP') {
      const s = session();
      const y = s ? readScroll(s, key) : null;
      if (y != null) restoreScroll(y);
    } else if (navType === 'PUSH' && pathChanged) {
      window.scrollTo(0, 0);
    }

    // Entrance (a View Transition already animated shell-link taps).
    if (takeViewTransitionFlag() || prefersReducedMotion() || !pathChanged) return;
    const content = document.querySelector<HTMLElement>('[data-adm-content]');
    content?.animate?.(entranceKeyframes(navType), { duration: ROUTE_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useSwipeBack(admin);
  const ptr = usePullToRefresh(admin);
  useTypingFlag(admin, location.pathname);

  if (!admin) return null;
  return <PullIndicator {...ptr} />;
}

/** Data may still be loading after Back: retry until the page is tall enough
 *  (≤ 1.5 s), and give up the moment the person touches or scrolls. */
function restoreScroll(y: number) {
  const until = Date.now() + 1500;
  let stop = false;
  const cancel = () => { stop = true; };
  window.addEventListener('touchstart', cancel, { once: true, passive: true });
  window.addEventListener('wheel', cancel, { once: true, passive: true });
  const tick = () => {
    if (stop) return;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo(0, Math.min(y, Math.max(0, max)));
    if (max >= y || Date.now() > until) {
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('wheel', cancel);
      return;
    }
    requestAnimationFrame(tick);
  };
  tick();
}

// ── Left-edge swipe-back ───────────────────────────────────────────────────
function useSwipeBack(active: boolean) {
  useEffect(() => {
    if (!active) return;
    if (!swipeBackEnabled({ touch: isTouch(), ios: isIosDevice(navigator), standalone: isStandalone(window) })) return;
    let g: null | {
      x0: number; y0: number; axis: Axis; main: HTMLElement; canGoBack: boolean;
      dx: number; lastX: number; lastT: number; vx: number; width: number;
    } = null;

    const clear = (main: HTMLElement) => {
      main.style.transition = ''; main.style.left = ''; main.style.position = ''; main.style.boxShadow = '';
    };

    const onStart = (e: Event) => {
      const p = pt(e);
      if (!p || (e as TouchLike).touches.length > 1 || !startsSwipeBack(p.clientX) || !isPhone() || sheetOpen()) return;
      const main = document.querySelector<HTMLElement>('[data-adm-main]');
      if (!main) return;
      g = {
        x0: p.clientX, y0: p.clientY, axis: 'pending', main, canGoBack: canGoBackInApp(window.history.state),
        dx: 0, lastX: p.clientX, lastT: Date.now(), vx: 0, width: window.innerWidth,
      };
    };
    const onMove = (e: Event) => {
      if (!g) return;
      const p = pt(e);
      if (!p) return;
      const dx = p.clientX - g.x0;
      const dy = p.clientY - g.y0;
      if (g.axis === 'pending') {
        g.axis = axisLock(dx, dy);
        if (g.axis === 'vertical') { g = null; return; }
        if (g.axis === 'pending') return;
      }
      e.preventDefault(); // horizontal: the page must not scroll under the finger
      const now = Date.now();
      const dt = Math.max(1, now - g.lastT);
      g.vx = (p.clientX - g.lastX) / dt;
      g.lastX = p.clientX; g.lastT = now; g.dx = dx;
      const off = swipeBackOffset(dx, g.canGoBack, g.width);
      g.main.style.transition = 'none';
      g.main.style.position = 'relative';
      g.main.style.left = `${Math.round(off)}px`;
      g.main.style.boxShadow = '-12px 0 24px -12px rgba(31,41,55,0.25)';
    };
    const onEnd = () => {
      if (!g) return;
      const cur = g; g = null;
      if (cur.axis !== 'horizontal') return;
      // A finger that stopped before lifting is not a flick.
      const vx = Date.now() - cur.lastT > 100 ? 0 : cur.vx;
      const reduced = prefersReducedMotion();
      if (shouldCommitBack({ dx: cur.dx, vx, width: cur.width, canGoBack: cur.canGoBack })) {
        const leave = () => {
          const reset = () => clear(cur.main);
          window.addEventListener('popstate', () => requestAnimationFrame(reset), { once: true });
          setTimeout(reset, 600);
          window.history.back();
        };
        if (reduced) { leave(); return; }
        cur.main.style.transition = 'left 180ms ease-in';
        cur.main.style.left = `${cur.width}px`;
        setTimeout(leave, 180);
      } else {
        cur.main.style.transition = reduced ? 'none' : 'left 200ms ease-out';
        cur.main.style.left = '0px';
        setTimeout(() => clear(cur.main), reduced ? 0 : 220);
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [active]);
}

// ── Pull-to-refresh ────────────────────────────────────────────────────────
interface PtrState { pull: number; refreshing: boolean }

function usePullToRefresh(active: boolean): PtrState {
  const qc = useQueryClient();
  const [state, setState] = useState<PtrState>({ pull: 0, refreshing: false });
  const refreshing = useRef(false);

  useEffect(() => {
    if (!active) return;
    let g: null | { x0: number; y0: number; axis: 'pending' | 'down'; pull: number } = null;

    const onStart = (e: Event) => {
      const p = pt(e);
      if (!p || refreshing.current || (e as TouchLike).touches.length > 1) return;
      if (!isPhone() || sheetOpen() || window.scrollY > 0 || startsSwipeBack(p.clientX)) return;
      if (ptrBlocked(e.target instanceof Element ? e.target : null)) return;
      g = { x0: p.clientX, y0: p.clientY, axis: 'pending', pull: 0 };
    };
    const onMove = (e: Event) => {
      if (!g) return;
      const p = pt(e);
      if (!p) return;
      const dx = p.clientX - g.x0;
      const dy = p.clientY - g.y0;
      if (g.axis === 'pending') {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (dy > 0 && dy > Math.abs(dx)) g.axis = 'down';
        else { g = null; return; }
      }
      if (window.scrollY > 1) { g = null; setState({ pull: 0, refreshing: false }); return; }
      g.pull = ptrPull(dy);
      setState({ pull: g.pull, refreshing: false });
    };
    const onEnd = () => {
      if (!g) return;
      const pull = g.pull; g = null;
      if (pull < PTR_THRESHOLD_PX) { setState({ pull: 0, refreshing: false }); return; }
      refreshing.current = true;
      setState({ pull: PTR_THRESHOLD_PX, refreshing: true });
      const work = qc.invalidateQueries({ refetchType: 'active' }).catch(() => {});
      const minSpin = new Promise((r) => setTimeout(r, 500));
      const cap = new Promise((r) => setTimeout(r, 10_000));
      void Promise.race([Promise.all([work, minSpin]), cap]).then(() => {
        refreshing.current = false;
        setState({ pull: 0, refreshing: false });
      });
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [active, qc]);

  return state;
}

/** A gold ring that slides out from under the phone header as you pull, its
 *  arrow turning; spinning while the data refetches. */
function PullIndicator({ pull, refreshing }: PtrState) {
  const { t } = useT();
  const progress = Math.min(1, pull / PTR_THRESHOLD_PX);
  const visible = pull > 0 || refreshing;
  return (
    <>
      <div
        aria-hidden
        className="md:hidden fixed left-1/2 z-[25] pointer-events-none"
        style={{
          top: 'calc(var(--adm-header-h) - 44px)',
          transform: `translate(-50%, ${Math.round(pull)}px)`,
          opacity: visible ? Math.max(0.25, progress) : 0,
          transition: pull === 0 || refreshing ? 'transform 200ms ease-out, opacity 200ms ease-out' : 'none',
        }}
      >
        <div className="w-10 h-10 rounded-full bg-white grid place-items-center shadow-[0_2px_10px_rgba(31,41,55,0.18)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.5)]" style={{ color: 'var(--color-adm-gold-deep)' }}>
          {refreshing ? (
            <span className="w-5 h-5 rounded-full border-2 border-current border-t-transparent animate-spin motion-reduce:animate-none" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="w-5 h-5"
                 style={{ transform: `rotate(${Math.round(progress * 180)}deg)` }}>
              <path d="M12 5v14M6 13l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
      <span role="status" className="sr-only">{refreshing ? t('Actualisation…') : ''}</span>
    </>
  );
}

// ── Keyboard flag ──────────────────────────────────────────────────────────
const TEXTY = 'textarea, select, [contenteditable="true"], input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="button"]):not([type="submit"]):not([type="file"]):not([type="color"])';

/** A field that brings up the keyboard. Not a keyboard-less field (inputmode="none":
 *  the scanner's Bluetooth field) and not one already gone from the page. */
function opensKeyboard(el: unknown): boolean {
  return el instanceof Element && el.isConnected && el.matches(TEXTY) && el.getAttribute('inputmode') !== 'none';
}

function useTypingFlag(active: boolean, pathname: string) {
  // A new page: iOS fires no focusout when the focused field leaves with the old page,
  // so re-check instead of trusting the last event (owner 2026-10-02: bar gone after the scanner).
  useEffect(() => {
    if (!opensKeyboard(document.activeElement)) document.documentElement.removeAttribute('data-adm-typing');
  }, [pathname]);
  useEffect(() => {
    const html = document.documentElement;
    if (!active) { html.removeAttribute('data-adm-typing'); return; }
    let tmr: ReturnType<typeof setTimeout> | undefined;
    const onIn = (e: FocusEvent) => {
      if (opensKeyboard(e.target) && isPhone()) {
        clearTimeout(tmr);
        html.setAttribute('data-adm-typing', '');
      }
    };
    // Focus moving from one field to the next must not flash the bar.
    const onOut = () => {
      clearTimeout(tmr);
      tmr = setTimeout(() => {
        const a = document.activeElement;
        if (!opensKeyboard(a)) html.removeAttribute('data-adm-typing');
      }, 30);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      clearTimeout(tmr);
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
      html.removeAttribute('data-adm-typing');
    };
  }, [active]);
}
